import * as React from "react";
import { cn } from "./utils";
import { Eye, Globe, Target, Users } from "lucide-react";

export interface FormPrevalenceBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    prevalence: string | null | undefined;
}

const prevalenceConfig = {
    ubiquitous: {
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Ubiquitous",
        icon: Globe,
    },
    common: {
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "Common",
        icon: Users,
    },
    uncommon: {
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Uncommon",
        icon: Eye,
    },
    rare: {
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Rare",
        icon: Target,
    },
    very_rare: {
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Very Rare",
        icon: Target,
    },
    "very rare": {
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Very Rare",
        icon: Target,
    },
    niche: {
        color: "bg-gray-100 text-gray-800 border-gray-200",
        label: "Niche",
        icon: Target,
    },
};

const FormPrevalenceBadge = React.forwardRef<HTMLSpanElement, FormPrevalenceBadgeProps>(
    ({ className, prevalence, ...props }, ref) => {
        // Handle null or undefined prevalence
        if (!prevalence) {
            return (
                <span
                    ref={ref}
                    className={cn(
                        "inline-flex items-center bg-gray-100 px-2 py-1 border border-gray-200 rounded font-medium text-gray-800 text-xs",
                        className
                    )}
                    {...props}
                >
                    -
                </span>
            );
        }

        const config = prevalenceConfig[prevalence?.toLowerCase()?.replace(/ /g, "_") as keyof typeof prevalenceConfig];

        if (!config) {
            return (
                <span
                    ref={ref}
                    className={cn(
                        "inline-flex items-center bg-gray-100 px-2 py-1 border border-gray-200 rounded font-medium text-gray-800 text-xs capitalize",
                        className
                    )}
                    {...props}
                >
                    {prevalence?.replace(/_/g, " ")}
                </span>
            );
        }

        const Icon = config.icon;

        return (
            <span
                ref={ref}
                className={cn(
                    "inline-flex items-center gap-1 shadow-sm px-2 py-1 border rounded-md font-semibold text-xs",
                    config.color,
                    className
                )}
                {...props}
            >
                {Icon && <Icon className="w-3 h-3" />}
                {config.label}
            </span>
        );
    }
);

FormPrevalenceBadge.displayName = "FormPrevalenceBadge";

export { FormPrevalenceBadge };
